import type { BaseClient } from '../../http/BaseClient.js';
import type {
  GetUsageSummaryQuery,
  GetUsageSummaryResponse,
  UsageByModelQuery,
  UsageByModelResponse,
  UsageReportQuery,
  UsageReportResponse,
  UsageSummaryQuery,
  UsageSummaryResponse,
} from './types.js';

const LEGACY_AGENT_PATH = '/api/agentengine/v1/usage'; // legacy proxy path
const SCOPED_BASE = '/usage'; // new backend-scoped surface (RBAC + scope routing)

export class UsageClient {
  constructor(private readonly http: BaseClient) {}

  // ── legacy ─────────────────────────────────────────────────────────────────
  // Existing Settings → Usage page calls this. Kept for back-compat until the
  // new dashboard fully replaces the JSON dump.
  getSummary(query: GetUsageSummaryQuery): Promise<GetUsageSummaryResponse> {
    return this.http.get<GetUsageSummaryResponse>(`${LEGACY_AGENT_PATH}/summary`, { query });
  }

  // ── new scoped surface ─────────────────────────────────────────────────────
  // Hits the backend-side RBAC wrapper; the backend re-injects the scope id
  // into the agent query so we don't have to.
  org(query: UsageSummaryQuery = {}): Promise<UsageSummaryResponse> {
    return this.http.get(`${SCOPED_BASE}/org`, { query: query as Record<string, string> });
  }
  project(projectId: string, query: UsageSummaryQuery = {}): Promise<UsageSummaryResponse> {
    return this.http.get(`${SCOPED_BASE}/project/${encodeURIComponent(projectId)}`, { query: query as Record<string, string> });
  }
  sandbox(sandboxId: string, query: UsageSummaryQuery = {}): Promise<UsageSummaryResponse> {
    return this.http.get(`${SCOPED_BASE}/sandbox/${encodeURIComponent(sandboxId)}`, { query: query as Record<string, string> });
  }
  chat(chatId: string, query: UsageSummaryQuery = {}): Promise<UsageSummaryResponse> {
    return this.http.get(`${SCOPED_BASE}/chat/${encodeURIComponent(chatId)}`, { query: query as Record<string, string> });
  }

  // Each report variant exists so the dashboard chart can request hourly /
  // daily buckets without leaking scope routing into the caller.
  orgReport(query: UsageReportQuery = {}): Promise<UsageReportResponse> {
    return this.http.get(`${SCOPED_BASE}/org`, { query: { ...(query as Record<string, string>), kind: 'report' } });
  }
  projectReport(projectId: string, query: UsageReportQuery = {}): Promise<UsageReportResponse> {
    return this.http.get(`${SCOPED_BASE}/project/${encodeURIComponent(projectId)}`, { query: { ...(query as Record<string, string>), kind: 'report' } });
  }
  sandboxReport(sandboxId: string, query: UsageReportQuery = {}): Promise<UsageReportResponse> {
    return this.http.get(`${SCOPED_BASE}/sandbox/${encodeURIComponent(sandboxId)}`, { query: { ...(query as Record<string, string>), kind: 'report' } });
  }
  chatReport(chatId: string, query: UsageReportQuery = {}): Promise<UsageReportResponse> {
    return this.http.get(`${SCOPED_BASE}/chat/${encodeURIComponent(chatId)}`, { query: { ...(query as Record<string, string>), kind: 'report' } });
  }

  // Per-model breakdown — drives the table under the bar chart.
  orgByModel(query: UsageByModelQuery = {}): Promise<UsageByModelResponse> {
    return this.http.get(`${SCOPED_BASE}/org`, { query: { ...(query as Record<string, string>), kind: 'by_model' } });
  }
  projectByModel(projectId: string, query: UsageByModelQuery = {}): Promise<UsageByModelResponse> {
    return this.http.get(`${SCOPED_BASE}/project/${encodeURIComponent(projectId)}`, { query: { ...(query as Record<string, string>), kind: 'by_model' } });
  }
  sandboxByModel(sandboxId: string, query: UsageByModelQuery = {}): Promise<UsageByModelResponse> {
    return this.http.get(`${SCOPED_BASE}/sandbox/${encodeURIComponent(sandboxId)}`, { query: { ...(query as Record<string, string>), kind: 'by_model' } });
  }
  chatByModel(chatId: string, query: UsageByModelQuery = {}): Promise<UsageByModelResponse> {
    return this.http.get(`${SCOPED_BASE}/chat/${encodeURIComponent(chatId)}`, { query: { ...(query as Record<string, string>), kind: 'by_model' } });
  }
}
